<template>
    <div>
        <BaseDialog
            v-model:open="isVisible"
            width="narrow"
            :show-close="false"
            :title="t('components.sessionExpiration.title')"
            :description="t('components.sessionExpiration.body')"
            disable-close-on-backdrop-click
            overlay-class="z-[80]"
            dialog-content-class="z-[90]">
            <p class="body text-ink-muted">{{ t("components.sessionExpiration.body") }}</p>
            <template #footer>
                <BaseButton theme="primary" @click="onRenewSession">{{ t("components.sessionExpiration.stillHere") }}</BaseButton>
            </template>
        </BaseDialog>

        <!-- Hidden logout form -->
        <form ref="logoutForm" method="POST" action="/accounts/logout/" style="display: none">
            <input type="hidden" name="csrfmiddlewaretoken" :value="getCookie('csrftoken')" />
            <input type="hidden" name="next" :value="nextUrl" />
        </form>
    </div>
</template>

<script setup>
import { usePage } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { discardBrowserHistory } from "~vue/utils/browserHistory";
import { shutdownIntercomMessengerEverywhere } from "~vue/utils/intercom";
import { reportSessionEnded, SESSION_EXPIRED } from "~vue/utils/sessionEnded";
import { DateTime } from "luxon";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { getCookie } from "../utils";

const { t } = useI18n();

/** How frequently to check for an about-to-expire session, in milliseconds. */
const SESSION_CHECK_INTERVAL_MS = 10_000;

/** The session warning dialog will appear this many milliseconds before the session expiration time. */
const SESSION_SHOW_WARNING_DIALOG_MS = 60_000;

const EXPIRATION_COOKIE_NAME = "session_expires_at";

// Mounted at the app root (not in a layout) so pages without the sidebar get the warning too, and
// on the same z-80/z-90 tier as SessionEndedDialog so it paints above side panels and other dialogs.
// Backdrop and escape are disabled on purpose: the only way out is renewing or being logged out.

const logoutForm = ref(null);
const timer = ref(null);
const isVisible = ref(false);

const nextUrl = computed(() => window.location.pathname + window.location.search);

async function checkExpiry() {
    const cookie = window.cookieStore && (await window.cookieStore.get(EXPIRATION_COOKIE_NAME));
    const expiresAt = cookie ? cookie.value : getCookie(EXPIRATION_COOKIE_NAME);
    if (!expiresAt) {
        return;
    }

    const now = DateTime.now();
    const sessionExpiresAt = DateTime.fromISO(expiresAt);
    if (sessionExpiresAt < now) {
        return reportExpired();
    }

    const msRemaining = sessionExpiresAt - now;
    isVisible.value = msRemaining <= SESSION_SHOW_WARNING_DIALOG_MS;
}

// setInterval is throttled and then fully paused when the browser freezes or discards a
// hidden tab (Chrome's Page Lifecycle), so a session that lapses while the user is away is
// never noticed by the timer alone. Re-check the instant the tab becomes visible again to
// catch an expiry that happened while it was frozen.
function onVisibilityChange() {
    if (document.visibilityState === "visible") {
        checkExpiry();
    }
}

onMounted(() => {
    timer.value = setInterval(checkExpiry, SESSION_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
});

onUnmounted(() => {
    clearInterval(timer.value);
    document.removeEventListener("visibilitychange", onVisibilityChange);
});

async function onRenewSession() {
    try {
        const response = await fetch("/api/accounts/renew-session", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: "{}",
        });
        if (!response.ok) {
            return logout();
        }
    } finally {
        isVisible.value = false;
    }
}

/**
 * Hand an already-expired session to SessionEndedDialog, which offers a way back in.
 *
 * Deliberately not the logout form: logging out is a CSRF-protected POST, and with
 * CSRF_USE_SESSIONS the secret lives in the session that has just expired -- so that POST is
 * rejected with a 403 and the user is stranded on an error page (BUG-942). There is nothing to
 * tear down server-side either, since the session is already gone.
 *
 * Not a direct navigation either. The expiry cookie is only refreshed on responses that also set
 * the session cookie, so it can read as past while the session is in fact still alive (likewise
 * under a fast client clock). Navigating on that would yank the user out of a page they were
 * still entitled to; a dialog leaves the decision, and the page, with them (BUG-947).
 */
function reportExpired() {
    clearInterval(timer.value);
    isVisible.value = false;
    reportSessionEnded(SESSION_EXPIRED, usePage().props?.session_ended_login_url ?? null, "client_timer");
}

function logout() {
    if (logoutForm.value) {
        discardBrowserHistory();
        shutdownIntercomMessengerEverywhere();
        logoutForm.value.submit();
    }
}
</script>
